import { User } from "@common/types/user";
import { TaskView } from "@common/types/views/tasksView";
import { NamedView } from "@common/types/views/views";
import { ORGANIZATION_TASK_LIST_CAPABILITIES } from "@components/tasks/taskListCapabilities";
import { ORGANIZATION_DEFAULT_TASK_LIST_QUERY } from "@components/tasks/taskListConstants";
import {
    buildFirstVisitLanding,
    MONTH_END_CLOSE_LANDING,
    TasksLanding,
} from "@components/tasks/tasksLanding";
import {
    buildMonthEndCloseView,
    MONTH_END_CLOSE_VIEW_ID,
} from "@components/tasks/views/OrganizationTaskViews";

/** The org default query with no view selected, as returning users land. */
export const NO_LANDING: TasksLanding = {
    landingViewId: undefined,
    defaultQuery: ORGANIZATION_DEFAULT_TASK_LIST_QUERY,
    initialOverlayFilters: undefined,
};

interface ResolveOrganizationTasksLandingParams {
    user: Pick<User, "id" | "type">;
    hasCachedState: boolean;
    /** Closes or close stages are on their first fetch, so the built-in views may still miss their columns. */
    isAwaitingFirstFetch: boolean;
}

/**
 * First visits land on Assigned to me, or on All tasks for support users, who
 * aren't members; returning users keep their cached view. `null` while a
 * first visit waits for the closes and close stages its view is built from.
 */
export function resolveOrganizationTasksLanding({
    user,
    hasCachedState,
    isAwaitingFirstFetch,
}: ResolveOrganizationTasksLandingParams): TasksLanding | null {
    if (hasCachedState) {
        return NO_LANDING;
    }
    if (isAwaitingFirstFetch) {
        return null;
    }
    return buildFirstVisitLanding({
        user,
        defaultStatuses: ORGANIZATION_TASK_LIST_CAPABILITIES.defaultStatuses,
    });
}

const CLIENT_LINK_VIEWS = {
    [MONTH_END_CLOSE_VIEW_ID]: {
        buildView: buildMonthEndCloseView,
        landing: MONTH_END_CLOSE_LANDING,
    },
} satisfies Record<
    string,
    { buildView: () => NamedView<TaskView>; landing: TasksLanding }
>;

/** Built-in views a client link can open on (see `getOrganizationClientTasksLink`). */
export type ClientTasksLinkViewId = keyof typeof CLIENT_LINK_VIEWS;

const isClientTasksLinkViewId = (
    viewId: string,
): viewId is ClientTasksLinkViewId => Object.hasOwn(CLIENT_LINK_VIEWS, viewId);

/** What a `?clientId=` link saves before the page content mounts. */
export interface ClientTasksLink {
    /** Its filters are the link's whole filter state, narrowed to the client. */
    landing: TasksLanding;
    /** The linked view, saved over the cached one; `undefined` keeps the user's display. */
    view: NamedView<TaskView> | undefined;
}

interface ResolveClientTasksLinkParams {
    clientId: string;
    /** The link's `view` param; a value that isn't a `ClientTasksLinkViewId` is ignored. */
    viewId: string | null | undefined;
    /** The link's `closeId` param, narrowing the filters to that close as well. */
    closeId?: string | null;
}

const narrowLandingToClient = ({
    landing,
    clientId,
    closeId,
}: {
    landing: TasksLanding;
    clientId: string;
    closeId: string | null | undefined;
}): TasksLanding => ({
    ...landing,
    defaultQuery: {
        ...landing.defaultQuery,
        clientIds: [clientId],
        ...(closeId ? { closeIds: [closeId] } : {}),
    },
});

/** A client link opens its view, or no view, filtered to the client and its close. */
export function resolveClientTasksLink({
    clientId,
    viewId,
    closeId,
}: ResolveClientTasksLinkParams): ClientTasksLink {
    if (!viewId || !isClientTasksLinkViewId(viewId)) {
        return {
            landing: narrowLandingToClient({
                landing: NO_LANDING,
                clientId,
                closeId,
            }),
            view: undefined,
        };
    }

    const { buildView, landing } = CLIENT_LINK_VIEWS[viewId];
    return {
        landing: narrowLandingToClient({ landing, clientId, closeId }),
        view: buildView(),
    };
}
